import React, { createContext, useState, useContext, ReactNode } from "react";
import { Project, VideoPage, Actor, Script, CustomPhotoAvatar, AvatarGroup } from "./types";
import { getActors } from "../client/api";

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  currentVideoPage: VideoPage | null;
  avatars: Actor[] | null;
  talkingPhotos: Actor[] | null;
  // Cached actors for ActorsDialog
  cachedCustomPhotoAvatars: CustomPhotoAvatar[] | null;
  setCachedCustomPhotoAvatars: React.Dispatch<React.SetStateAction<CustomPhotoAvatar[] | null>>;
  cachedAvatarGroups: AvatarGroup[] | null;
  setCachedAvatarGroups: React.Dispatch<React.SetStateAction<AvatarGroup[] | null>>;
  // Script management
  currentScript: Script | null;
  setCurrentScript: React.Dispatch<React.SetStateAction<Script | null>>;
  // Existing methods
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
  setCurrentProject: React.Dispatch<React.SetStateAction<Project | null>>;
  setCurrentVideoPage: React.Dispatch<React.SetStateAction<VideoPage | null>>;
  fetchAvatars: (organizationId: string, workspaceId: string) => Promise<void>;
  updateScriptContent: (updates: Partial<Script>) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProjectContext must be used within a ProjectProvider");
  }
  return context;
};

interface ProjectProviderProps {
  children: ReactNode;
  // Optional initial values for script sections
  initialScript?: {
    hook?: string;
    body?: string;
    cta?: string;
  };
}

export const ProjectProvider = ({ children }: ProjectProviderProps) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [currentVideoPage, setCurrentVideoPage] = useState<VideoPage | null>(
    null
  );
  const [avatars, setAvatars] = useState<Actor[] | null>(null);
  const [talkingPhotos, setTalkingPhotos] = useState<Actor[] | null>(null);

  // Cached actors for ActorsDialog - persist across dialog open/close
  const [cachedCustomPhotoAvatars, setCachedCustomPhotoAvatars] = useState<CustomPhotoAvatar[] | null>(null);
  const [cachedAvatarGroups, setCachedAvatarGroups] = useState<AvatarGroup[] | null>(null);

  // New state for script sections with initial values
  const [currentScript, setCurrentScript] = useState<Script | null>(null);

  const updateScriptContent = (updates: Partial<Script>) => {
    setCurrentScript((prev) => {
      if (!prev) return null;
      return { ...prev, ...updates };
    });
  };

  const fetchAvatars = async (organizationId: string, workspaceId: string) => {
    try {
      const data = await getActors(organizationId, workspaceId);
      const avatars = data.data.avatars.map((actor: any) => ({
        ...actor,
        avatar_states: [{ id: actor.avatar_id }],
      }));
      const photos = data.data.talking_photos.map((photo: any) => ({
        avatar_id: photo.talking_photo_id,
        avatar_name: photo.talking_photo_name,
        preview_image_url: photo.preview_image_url,
        preview_video_url: photo.preview_image_url,
        isTalkingPhoto: true,
        avatar_states: [{ id: photo.talking_photo_id }],
        gender: "unknown",
        name: photo.talking_photo_name,
        voiceName: "Default",
      }));
      setAvatars(avatars);
      setTalkingPhotos(photos);
    } catch (error) {
      console.error("Error fetching avatars: ", error);
    }
  };

  return (
    <ProjectContext.Provider
      value={{
        projects,
        currentProject,
        currentVideoPage,
        avatars,
        talkingPhotos,
        cachedCustomPhotoAvatars,
        setCachedCustomPhotoAvatars,
        cachedAvatarGroups,
        setCachedAvatarGroups,
        currentScript,
        setCurrentScript,
        setProjects,
        setCurrentProject,
        setCurrentVideoPage,
        fetchAvatars,
        updateScriptContent,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};
